import React, { useState } from 'react';
import { X, CircleNotch, Lightning, Microphone } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { toast } from 'sonner';

const CREDIT_PACKS = [
  { key: 'credits_1000', label: '1,000 credits', price: 29, highlight: false },
  { key: 'credits_5000', label: '5,000 credits', price: 99, highlight: true },
  { key: 'credits_20000', label: '20,000 credits', price: 299, highlight: false },
];

const VOICE_PACKS = [
  { key: 'voice_100', label: '100 minutes', price: 19, highlight: false },
  { key: 'voice_500', label: '500 minutes', price: 79, highlight: true },
  { key: 'voice_1500', label: '1,500 minutes', price: 199, highlight: false },
];

export const TopUpModal = ({ isOpen, onClose, defaultTab = 'credits' }) => {
  const [tab, setTab] = useState(defaultTab);
  const [processing, setProcessing] = useState(null);
  const { activeOrganization } = useOrganization();

  if (!isOpen) return null;

  const handlePurchase = async (addonType, addonKey) => {
    setProcessing(addonKey);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (activeOrganization?.organization_id) {
        headers['x-org-id'] = activeOrganization.organization_id;
      }
      const res = await fetch(`${BACKEND_URL}/api/pricing/checkout/addon`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          addon_type: addonType,
          addon_key: addonKey,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (data.url || data.checkout_url) {
        window.location.href = data.url || data.checkout_url;
      } else {
        toast.error(data.detail || data.error || 'Could not start checkout');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setProcessing(null);
    }
  };

  const packs = tab === 'credits' ? CREDIT_PACKS : VOICE_PACKS;
  const addonType = tab === 'credits' ? 'credit_pack' : 'voice_pack';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="topup-modal-overlay"
    >
      <div
        className="bg-[#111820] border border-[rgba(255,255,255,0.08)] rounded-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="topup-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <h2 className="text-lg font-semibold text-white">Top up your account</h2>
          <button
            onClick={onClose}
            className="text-[#7D8896] hover:text-white transition-colors p-1"
            data-testid="topup-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-2 pb-4">
          <button
            onClick={() => setTab('credits')}
            data-testid="topup-tab-credits"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
              tab === 'credits'
                ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0]'
                : 'text-[#7D8896] hover:text-[#B0B8C4] hover:bg-[rgba(255,255,255,0.04)]'
            }`}
          >
            <Lightning size={14} weight="fill" /> Credits
          </button>
          <button
            onClick={() => setTab('voice')}
            data-testid="topup-tab-voice"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
              tab === 'voice'
                ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0]'
                : 'text-[#7D8896] hover:text-[#B0B8C4] hover:bg-[rgba(255,255,255,0.04)]'
            }`}
          >
            <Microphone size={14} weight="fill" /> Voice Minutes
          </button>
        </div>

        {/* Pack Options */}
        <div className="px-6 pb-6 space-y-3">
          {packs.map((pack) => (
            <button
              key={pack.key}
              onClick={() => handlePurchase(addonType, pack.key)}
              disabled={processing === pack.key}
              data-testid={`topup-pack-${pack.key}`}
              className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                pack.highlight
                  ? 'bg-[rgba(0,229,192,0.06)] border border-[#00E5C0]/20 hover:border-[#00E5C0]/40'
                  : 'bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.12)]'
              } ${processing === pack.key ? 'opacity-60' : ''}`}
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-white">{pack.label}</span>
                {pack.highlight && (
                  <span className="text-[10px] font-semibold text-[#00E5C0] bg-[rgba(0,229,192,0.08)] px-2 py-0.5 rounded-full">
                    Best value
                  </span>
                )}
              </div>
              <span className="text-sm font-bold text-white">
                {processing === pack.key ? (
                  <CircleNotch size={16} className="animate-spin text-[#00E5C0]" />
                ) : (
                  `$${pack.price}`
                )}
              </span>
            </button>
          ))}
        </div>

        {/* Footer hint */}
        <div className="px-6 pb-5">
          <p className="text-[11px] text-[#4B5563] text-center">
            Top-up credits never expire. Secure checkout powered by Stripe.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TopUpModal;
